import React from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';

import { FrictionlessDetailsRequired } from './FrictionlessDetailsRequired';
import { FrictionlessDisclaimer } from './FrictionlessDisclaimer';

import { Mobile } from 'components/Shared/Icons/Icons';
import InfoCircle from 'components/Shared/Icons/InfoCircle';
import Switch from 'components/Shared/Inputs/Switch';
import CpPopover from 'components/Shared/Popover/CpPopover';
import useComponentVisible from 'hooks/useComponentVisible';
import {
  isFrictionlessCheckoutDetailsRequiredSelector,
  isFrictionlessCheckoutSelectedSelector,
} from 'store/auth/selectors';
import { authActions } from 'store/auth/slice';
import { useSelector } from 'store/utils';
import { colors, fontSize } from 'styles/cp';

interface FrictionlessCheckoutProps {
  cdn?: string;
  updateFrictionlessLoginCountry?: (country: Record<string, any>) => void;
}

const FrictionlessCheckout: React.FC<FrictionlessCheckoutProps> = ({
  updateFrictionlessLoginCountry,
  cdn,
}) => {
  const dispatch = useDispatch();
  const {
    ref: infoIconRef,
    isComponentVisible: isInfoTooltipOpen,
    setIsComponentVisible: setIsInfoTooltipOpen,
  } = useComponentVisible(false);
  const isFrictionlessCheckoutSelected = useSelector(isFrictionlessCheckoutSelectedSelector);
  const isFrictionlessCheckoutDetailsRequired = useSelector(
    isFrictionlessCheckoutDetailsRequiredSelector
  );

  const handleChange = () => {
    dispatch(
      authActions.setFrictionlessCheckoutSelected({
        frictionlessCheckoutSelected: !isFrictionlessCheckoutSelected,
      })
    );
  };

  const handleHide = () => {
    dispatch(
      authActions.setFrictionlessCheckoutSelected({
        frictionlessCheckoutSelected: false,
      })
    );
    dispatch(
      authActions.setFrictionlessCheckoutDetailsRequired({
        isDetailsRequired: false,
      })
    );
    dispatch(
      authActions.setFrictionlessCheckoutDetailsModalClosed({
        isDetailsModalClosed: true,
      })
    );
  };

  return (
    <React.Fragment>
      <div className="frictionless-checkout">
        <div className="frictionless-checkout--header">
          <FormattedMessage
            id="FRICTIONLESS_SAVE_INFO"
            defaultMessage="Save my info for a faster checkout"
          />
          <div>
            <Switch checked={isFrictionlessCheckoutSelected} onChange={handleChange} />
          </div>
        </div>
        <div className="frictionless-checkout--content">
          <ul>
            <li>
              <FormattedMessage
                id="FRICTIONLESS_SAVE_INFO_BENEFIT_1"
                defaultMessage="1-click, passwordless checkout"
              />
            </li>
            <li>
              <FormattedMessage
                id="FRICTIONLESS_SAVE_INFO_BENEFIT_2"
                defaultMessage="Save payment info with an Intuit Account"
              />
            </li>
            <li>
              <div className="li-content">
                <span className="benefit-text">
                  <FormattedMessage
                    id="FRICTIONLESS_SAVE_INFO_BENEFIT_3"
                    defaultMessage="Let Intuit businesses know you're ready for online payments"
                  />
                </span>
                <span
                  ref={infoIconRef}
                  id="frictionless-info-icon"
                  className="info-icon"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsInfoTooltipOpen(!isInfoTooltipOpen);
                  }}
                  onMouseEnter={() => setIsInfoTooltipOpen(true)}
                  onMouseLeave={() => setIsInfoTooltipOpen(false)}
                >
                  <InfoCircle width={16} height={16} color={colors.gray02} />
                </span>
              </div>
              {isInfoTooltipOpen && (
                <CpPopover
                  innerClassName="cp-tooltip frictionless-info-tooltip"
                  placement="top-start"
                  target="frictionless-info-icon"
                  isOpen={isInfoTooltipOpen}
                  offset="0, 6"
                >
                  <span>
                    <FormattedMessage
                      id="FRICTIONLESS_INFO_ICON_TOOLTIP"
                      defaultMessage="Intuit businesses will see you saved payment methods. We won't share your payment details."
                    />
                  </span>
                </CpPopover>
              )}
            </li>
          </ul>
        </div>
        {isFrictionlessCheckoutSelected && (
          <div className="frictionless-checkout--footer">
            <div className="mobile-icon">
              <Mobile width={14} height={20} color={colors.pepper50} />
            </div>
            <FormattedMessage
              id="FRICTIONLESS_PHONE_VALIDATION_TEXT"
              defaultMessage="After selecting “Pay”, we’ll send a code to your phone to quickly verify it’s you and complete your account. Standard call or SMS rates may apply."
            />
          </div>
        )}
      </div>
      {isFrictionlessCheckoutSelected && <FrictionlessDisclaimer />}
      {isFrictionlessCheckoutDetailsRequired && (
        <FrictionlessDetailsRequired
          onHide={handleHide}
          updateFrictionlessLoginCountry={updateFrictionlessLoginCountry}
          cdn={cdn}
        />
      )}
      {/*language=SCSS*/}
      <style jsx>{`
        .frictionless-checkout {
          margin: 20px 0;
          border: 1px solid ${colors.gray05};
          border-radius: 8px;
          overflow: hidden;

          &--header {
            padding: 10px 14px;
            display: flex;
            justify-content: space-between;
            font-family: AvenirNextforINTUIT-Demi;
            font-size: ${fontSize.xs};
            border-bottom: 1px solid ${colors.gray05};
          }
          &--content {
            font-size: ${fontSize.xxs};
            padding: 10px 14px;
            ul {
              padding: 0;
              margin: 6px 18px 0 18px;
              li {
                margin-bottom: 6px;
                .li-content {
                  display: inline;
                  .benefit-text {
                    display: inline;
                  }
                }
                .info-icon {
                  display: inline-flex;
                  margin-left: 4px;
                  align-items: center;
                  vertical-align: middle;
                }
              }
            }
          }
          &--footer {
            display: flex;
            background: ${colors.stoneWhite};
            font-size: ${fontSize.xxxs};
            color: ${colors.gray02};
            padding: 10px 14px;
            .mobile-icon {
              margin: 5px 20px 0 0;
            }
          }
        }
        .disclaimer {
          font-size: ${fontSize.xxs};
          color: ${colors.gray02};
          margin: 25px 0 15px 0;
          text-align: center;
          a {
            color: ${colors.blue};
            text-decoration: underline;
          }
        }
      `}</style>
      {/*language=CSS*/}
      <style jsx global>{`
        .frictionless-info-tooltip {
          max-width: 290px !important;
          width: max-content !important;
          font-size: ${fontSize.xxs} !important;
        }
      `}</style>
    </React.Fragment>
  );
};

export { FrictionlessCheckout };
